import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, AlertCircle, Clock, ArrowRight } from 'lucide-react';
import { billingApi } from '../lib/billingApi';
import { useT } from '../lib/i18n';
import { isAnyStoreContext } from '../lib/platform';

// Same cache key as SubscriptionGate — single source of truth for the last
// known subscription status. Read-only here.
const CACHE_KEY = 'sp.lastSubStatus.v1';
const CACHE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

function readCachedStatus() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { status, ts } = JSON.parse(raw);
    if (!status || !ts) return null;
    if (Date.now() - ts > CACHE_MAX_AGE_MS) return null;
    return status;
  } catch { return null; }
}

function daysLeft(isoDate) {
  if (!isoDate) return null;
  const ms = new Date(isoDate).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86400000));
}

export function SubscriptionBanner() {
  const [status, setStatus] = useState(null);
  const { lang } = useT();
  const isEN = lang === 'en';

  useEffect(() => {
    // Offline: utiliser le cache directement, surtout pas montrer
    // "Démarrer essai gratuit" à un user qui a déjà payé.
    if (!navigator.onLine) {
      setStatus(readCachedStatus() || { status: 'none', _offline: true });
      return;
    }
    billingApi.status().then(setStatus).catch(() => {
      // Échec réseau alors qu'on était online → fallback cache si dispo.
      const cached = readCachedStatus();
      setStatus(cached || { status: 'none', _offline: true });
    });
  }, []);

  if (!status) return null;
  // En mode offline sans cache valide, on cache la bannière plutôt que
  // d'afficher un faux statut (qui pourrait pousser à payer inutilement).
  if (status._offline && status.status === 'none') return null;
  // Apple Guideline 3.1.1 / Google Play Billing — masquer toute CTA d'achat
  // dans les apps de magasin (iOS standalone, TWA Play Store, ET Capacitor
  // natif iOS/Android — sinon le texte "sans carte de crédit" contredit le
  // flow IAP Apple et risque rejet 3.1.1).
  if (isAnyStoreContext()) return null;
  const s = status.status;

  // Aucun abonnement / paiement abandonné → invite à voir les plans
  if (!s || s === 'none' || s === 'pending_checkout' || s === 'canceled' || s === 'incomplete') {
    return (
      <Link
        to="/pricing"
        data-testid="subscription-banner-upgrade"
        className="block rounded-xl p-3.5 mb-4"
        style={{ background: 'linear-gradient(135deg, #7A1F2B 0%, #5A1620 100%)' }}
      >
        <div className="flex items-center justify-between gap-3 text-white">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#C9A961' }}>
              <Sparkles className="w-4 h-4" style={{ color: '#5A1620' }} />
            </div>
            <div className="min-w-0">
              <div className="font-bold text-sm">
                {isEN ? 'Free trial — 3 days' : 'Essai gratuit — 3 jours'}
              </div>
              <div className="text-xs opacity-90 truncate">
                {isEN ? 'Unlock all features — no credit card required' : 'Active toutes les fonctionnalités sans carte de crédit'}
              </div>
            </div>
          </div>
          <ArrowRight className="w-4 h-4 flex-shrink-0" />
        </div>
      </Link>
    );
  }

  // Essai en cours → afficher le nombre de jours restants
  if (s === 'trialing') {
    const left = daysLeft(status.trial_end);
    // Si c'est un auto-trial (pas encore d'abo Stripe), pousser vers /pricing
    // pour convertir. Sinon vers /profile pour gérer un abo existant.
    const target = status.auto_trial ? '/pricing' : '/profile';
    const subText = status.auto_trial
      ? (isEN ? 'Choose your plan before the trial ends to keep your access' : 'Choisis ton plan avant la fin pour continuer')
      : (isEN ? 'Manage your subscription in your profile' : 'Gère ton abonnement dans ton profil');
    const daysLabel = left == null
      ? (isEN ? 'In progress' : 'En cours')
      : (left === 1
          ? (isEN ? '1 day left' : '1 jour restant')
          : (isEN ? `${left} days left` : `${left} jours restants`));
    return (
      <Link
        to={target}
        data-testid="subscription-banner-trial"
        className="block rounded-xl p-3.5 mb-4 border"
        style={{ backgroundColor: '#FAF7F2', borderColor: '#C9A961' }}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#C9A961' }}>
              <Clock className="w-4 h-4" style={{ color: '#5A1620' }} />
            </div>
            <div className="min-w-0">
              <div className="font-bold text-sm" style={{ color: '#5A1620' }}>
                {isEN ? 'Free trial' : 'Essai gratuit'} — {daysLabel}
              </div>
              <div className="text-xs" style={{ color: '#6B7280' }}>{subText}</div>
            </div>
          </div>
          <ArrowRight className="w-4 h-4 flex-shrink-0" style={{ color: '#7A1F2B' }} />
        </div>
      </Link>
    );
  }

  // Paiement échoué ou impayé → bannière rouge urgente
  if (s === 'past_due' || s === 'unpaid') {
    return (
      <Link
        to="/profile"
        data-testid="subscription-banner-past-due"
        className="block rounded-xl p-3.5 mb-4 border-2"
        style={{ backgroundColor: '#FEF2F2', borderColor: '#DC2626' }}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#DC2626' }}>
              <AlertCircle className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0">
              <div className="font-bold text-sm" style={{ color: '#991B1B' }}>
                {isEN ? 'Payment failed' : 'Paiement en échec'}
              </div>
              <div className="text-xs" style={{ color: '#7F1D1D' }}>
                {isEN ? 'Update your card to keep your access' : 'Mets à jour ta carte pour conserver ton accès'}
              </div>
            </div>
          </div>
          <ArrowRight className="w-4 h-4 flex-shrink-0" style={{ color: '#DC2626' }} />
        </div>
      </Link>
    );
  }

  // Abonnement actif → pas de bannière (on évite de polluer le tableau de bord)
  return null;
}
